import { lazy, SetStateAction, Suspense, useEffect, useMemo, useState } from "react";
import { EmfWidgetConfig, EmfWidgetDbModel, EmfWidgetType, IEmfWidget } from "./widget.models";
import { WidgetService } from "./widget.service";
import errorDescription from "../_shared/error-description";

const widgetComponents: Record<string, React.LazyExoticComponent<React.FC<IEmfWidget>>> = {
    [EmfWidgetType.new_business_vs_renewal_premium]: lazy(() => import('./../new-business-vs-renewal-premium/new-business-vs-renewal-premium.component')),
    [EmfWidgetType.conversion_rates]: lazy(() => import('./../conversion-rates/conversion-rates.widget.component')),
    [EmfWidgetType.risk_appetite_rates]: lazy(() => import('./../risk-appetite-rates/risk-appetite-rates.widget.component')),
    [EmfWidgetType.risk_appetite_ratio]: lazy(() => import('./../risk-appetite-ratio/risk-appetite-ratio.widget.component')),
    [EmfWidgetType.quoted_premium_vs_bond_premium]: lazy(() => import('./../quoted-vs-bond-premium/quoted-vs-bond-premium.widget.component')),
    [EmfWidgetType.business_flow]: lazy(() => import('./../business-flow/business-flow.widget.component')),
    [EmfWidgetType.risk_status_and_esitmated_premium]: lazy(() => import('./../risk-status-and-esitmated-premium/risk-status-and-estimated-premium.widget.component')),
    [EmfWidgetType.exposure_capacity_warnings]: lazy(() => import('./../exposure-capacity-warnings/exposure-capacity-warnings.widget.component')),
    [EmfWidgetType.premium_vs_target]: lazy(() => import('./../premium-vs-target/premium-vs-target.component')),
    [EmfWidgetType.ingestion_team]: lazy(() => import('./../ingestion-team/ingestion-team.widget.component')),
    [EmfWidgetType.ingestion_accuracy]: lazy(() => import('./../ingestion-accuracy/ingestion-accuracy.widget.component')),
    [EmfWidgetType.submission_score]: lazy(() => import('./../submission-score/submission-score.widget.component')),
    [EmfWidgetType.marine_team]: lazy(() => import('./../marine-team/marine-team.widget.component')),
    [EmfWidgetType.license_allocation]: lazy(() => import('./../product-license/license-allocation.widget.component')),
    [EmfWidgetType.license_usage]: lazy(() => import('./../product-license/license-usage.widget.component')),
    [EmfWidgetType.stacked_bar_chart]: lazy(() => import('./../stacked-bar-chart/stacked-bar-chart.widget.component')),
    [EmfWidgetType.cybercube_risk_score]: lazy(() => import('./../cybercube-risk/cybercube-risk-score.widget.component')),
    [EmfWidgetType.submission_metrics]: lazy(() => import('./../contract-metrics/submission-metrics.widget.component')),
    [EmfWidgetType.quote_metrics]: lazy(() => import('./../contract-metrics/quote-metrics.widget.component')),
    [EmfWidgetType.jtbd_metrics]: lazy(() => import('./../contract-metrics/jtbd-metrics.widget.component')),
    [EmfWidgetType.broker_submission_count_trend]: lazy(() => import('./../broker-count-trend/submission-count-trend.component')),
    [EmfWidgetType.broker_policy_count_trend]: lazy(() => import('./../broker-count-trend/policy-count-trend.component')),
    [EmfWidgetType.broker_quote_count_trend]: lazy(() => import('./../broker-count-trend/quote-count-trend.component')),
    [EmfWidgetType.premium_paid_info]: lazy(() => import('./../premium-paid-info/premium-paid-info.widget.component')),
    [EmfWidgetType.recently_booked_risk]: lazy(() => import('./../recently-booked/recently-booked.widget.component')),
    [EmfWidgetType.renewal_history]: lazy(() => import('./../renewal-history/renewal-history.widget.component')),
    [EmfWidgetType.tasks]: lazy(() => import('./../tasks/tasks.widget.component')),
    [EmfWidgetType.cybercube_risk_signals]: lazy(() => import('./../cybercube-risk/cybercube-risk-signals.widget.component')),
    [EmfWidgetType.broker_comparison_quote_conversion_rate_ranking]: lazy(() => import('./../broker-comparison/quote-conversion-rate.component')),
    [EmfWidgetType.broker_comparison_policy_conversion_rate_ranking]: lazy(() => import('./../broker-comparison/policy-conversion-rate.component')),
    [EmfWidgetType.broker_comparison_submission_rankings]: lazy(() => import('./../broker-comparison/submission-rankings.component')),
    [EmfWidgetType.broker_comparison_submission_appetite_rate_ranking]: lazy(() => import('./../broker-comparison/submission-appetite-rate-ranking.component')),
    [EmfWidgetType.top_brokers]: lazy(() => import('./../top-brokers/top-brokers.widget.component')),
    [EmfWidgetType.top_countries]: lazy(() => import('./../top-countries/top-countries.widget.component')),
    [EmfWidgetType.new_and_renewals_by_priority]: lazy(() => import('./../tasks-and-risks/new-and-renewals-by-priority')),
    [EmfWidgetType.new_and_renewals_by_key_account]: lazy(() => import('./../tasks-and-risks/new-and-renewals-by-key-account')),
    [EmfWidgetType.urgent_task]: lazy(() => import('./../tasks-and-risks/urgent-task')),
    [EmfWidgetType.global_disaster_map]: lazy(() => import('./../global-disater-map/global-disater-map.widget.component')),
    [EmfWidgetType.dnb]: lazy(() => import('./../dnb/dnb.widget.component')),
    [EmfWidgetType.news]: lazy(() => import('./../news/news.widget.component')),
    [EmfWidgetType.risk_additional_info]: lazy(() => import('./../additional-information/additional-information.widget.component')),
    [EmfWidgetType.risk_premium_summary]: lazy(() => import('./../additional-information/premium-summary.widget.component')),
    [EmfWidgetType.underwriting_details]: lazy(() => import('./../product-details/underwriting-details.widget.component')),
    [EmfWidgetType.exact_details]: lazy(() => import('./../product-details/exact-details.widget.component')),
    [EmfWidgetType.industry_classification]: lazy(() => import('./../industry-classification/industry-classification.widget.component')),
    [EmfWidgetType.risk_information]: lazy(() => import('./../risk-information/risk-information.widget.component')),
    [EmfWidgetType.knowledgegraphdot]: lazy(() => import('./../knowledge-graph/knowledge-graph.dot.widget.component')),
    [EmfWidgetType.knowledgegraph]: lazy(() => import('./../knowledge-graph/knowledge-graph.published.widget.component')),
    [EmfWidgetType.llm]: lazy(() => import('./../llm/llm.widget.component')),
    [EmfWidgetType.powerbi]: lazy(() => import('./../powerbi/powerbi.widget.wrapper.component')),
    [EmfWidgetType.qliksense]: lazy(() => import('./../qliksense/qliksense.widget.component')),
    [EmfWidgetType.qliksense_embed]: lazy(() => import('./../qliksense/qliksense.widget.component')),
    [EmfWidgetType.qliksense_iframe]: lazy(() => import('./../qliksense/qliksense.widget.component')),
    [EmfWidgetType.widgetgroup]: lazy(() => import('./widget.group.component')),

    [EmfWidgetType.submission_conversion_rates]: lazy(() => import('./../conversion-rates/submission-conversion-rates.component')),
    [EmfWidgetType.quote_conversion_rates]: lazy(() => import('./../conversion-rates/quote-conversion-rates.component')),
    [EmfWidgetType.risk_retention_rates]: lazy(() => import('./../conversion-rates/risk-retention-rates.component')),

    [EmfWidgetType.risk_status_and_esitmated_premium_v2]: lazy(() => import('../risk-status-and-esitmated-premium/risk-status-and-estimated-premium-v2.widget.component')),

    [EmfWidgetType.broker_submission_count_trend_v2]: lazy(() => import('./../broker-count-trend/submission-count-trend-mcdr.component')),
    [EmfWidgetType.broker_policy_count_trend_v2]: lazy(() => import('./../broker-count-trend/policy-count-trend-mcdr.component')),
    [EmfWidgetType.quote_issued_count_trend_v2]: lazy(() => import('./../broker-count-trend/quote-count-trend-mcdr.component')),
    [EmfWidgetType.broker_policy_gwp_trend_v2]: lazy(() => import('./../broker-count-trend/policy-gwp-count-trend-mcdr.component')),
  
    [EmfWidgetType.new_business_vs_renewal_premium_v2]: lazy(() => import('../new-business-vs-renewal-premium/new-business-vs-renewal-premium-v2.widget.component')),

    [EmfWidgetType.broker_comparison_quote_conversion_rate_ranking_mcdr]: lazy(() => import('./../broker-comparison-mcdr/quote-conversion-rate-mcdr.component')),
    [EmfWidgetType.broker_comparison_policy_conversion_rate_ranking_mcdr]: lazy(() => import('./../broker-comparison-mcdr/policy-conversion-rate-mcdr.component')),
    [EmfWidgetType.broker_comparison_submission_rankings_mcdr]: lazy(() => import('./../broker-comparison-mcdr/submission-rankings-mcdr.component')),
    [EmfWidgetType.broker_comparison_submission_appetite_rate_ranking_mcdr]: lazy(() => import('./../broker-comparison-mcdr/submission-appetite-rate-ranking-mcdr.component')),
  
};

export const WidgetComponent = (props: IEmfWidget) => {

    const widgetService = useMemo(() => WidgetService.getInstance(), []);
    widgetService.prepareAttributesKeyValue(props.config);
    const widgetType = props.config.widgetType;
    const SelectedWidget = widgetComponents[widgetType];

    const [widgetConfig, setWidgetConfig] = useState<EmfWidgetConfig>(props.config);

    const getWidgetDetailsAndSetConfig = () => {
        if (props.config.widgetType && props.config.widgetType != EmfWidgetType.widgetgroup) {
            widgetService.getWidgetDetails(props.config).then(result => {
                if (result && result.data) {
                    const res = result.data;
                    if (!props.config.title) { props.config.title = res.title; }
                    if (!props.config.subTitle) { props.config.subTitle = res.subTitle; }
                    props.config.expand = res.config.expand;
                    props.config.filter = res.config.filter;
                    props.config.footer = res.config.footer;
                    props.config.header = res.config.header;
                    props.config.preference = res.config.preference;
                    props.config.preferencePosition = res.config.preferencePosition || 'bottom';
                    props.config.refresh = res.config.refresh;
                    props.config.setting = res.config.setting;
                    props.config.visualization = res.config.visualization;
                    props.config.padding = (props.config.forcePadding ? true : res.config.padding);
                    props.config.border = (props.config.forceBorder ? true : res.config.border);
                    const updatedConfig = { ...props.config }
                    setWidgetConfig(updatedConfig);
                }
            })
        }
    }

    useEffect(() => {
        getWidgetDetailsAndSetConfig();
    }, [])

    useEffect(() => {
        const newConfig = {
            ...widgetConfig,
            theme: props.config.theme,
            title: props.config.title,
            subTitle: props.config.subTitle,
            primaryColor: props.config.primaryColor
        }
        setWidgetConfig(newConfig);
    }, [props.config])

    const onAttrChange = (newAttr: SetStateAction<string>) => {
        const config = new EmfWidgetConfig();
        let attrKeyValue = widgetService.parseAttributes(newAttr.toString());
        config.attrKeyValue = attrKeyValue;
        widgetService.prepareAttributesKeyValue(config);
        if (config) {
            const newConfig = {
                ...widgetConfig,
                theme: config.theme,
                title: config.title,
                subTitle: config.subTitle,
                primaryColor: config.primaryColor
            }
            setWidgetConfig(newConfig);
        }
    }
    widgetService.registerPropsChangeCallback(props.config.div, onAttrChange);


    return (
        <Suspense fallback={<div className="emf-w-full emf-h-full emf-flex emf-items-center emf-place-content-center">Loading widget...</div>}>
            {SelectedWidget ? <SelectedWidget config={widgetConfig} /> : errorDescription("Unsupported widget type, " + widgetType)}
        </Suspense>
    );
};


